import { type ReactNode, createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDialogs } from './Dialogs';
import { useToast } from './Toasts';

interface Entry {
  dirty: boolean;
  bar: boolean;
  save?: () => void | Promise<void>;
  discard?: () => void;
}
interface Api {
  register(id: string, e: Entry): void;
  unregister(id: string): void;
  isDirty(): boolean;
  /** Runs `fn` after the user resolves unsaved changes (Keep editing / Discard / Save & continue). */
  guard(fn: () => void): Promise<void>;
  saveAll(): Promise<void>;
  discardAll(): void;
}
const Ctx = createContext<Api | null>(null);

export function useUnsavedApi(): Api {
  const c = useContext(Ctx);
  if (!c) throw new Error('useUnsavedApi() must be used inside <UnsavedChangesProvider>');
  return c;
}

/**
 * Registers a form's dirty state. `bar: true` shows the sticky "Unsaved changes · Discard · Save" bar.
 *   useUnsavedChanges(isDirty, { onSave: submit, onDiscard: reset, bar: true })
 */
export function useUnsavedChanges(dirty: boolean, opts: { onSave?: () => void | Promise<void>; onDiscard?: () => void; bar?: boolean } = {}): void {
  const api = useUnsavedApi();
  const id = useId();
  const latest = useRef(opts);
  latest.current = opts;
  useEffect(() => {
    api.register(id, { dirty, bar: !!latest.current.bar, save: () => latest.current.onSave?.(), discard: () => latest.current.onDiscard?.() });
  }, [api, id, dirty]);
  useEffect(() => () => api.unregister(id), [api, id]);
}

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const entries = useRef(new Map<string, Entry>());
  const [barVisible, setBarVisible] = useState(false);
  const dialogs = useDialogs();
  const toast = useToast();

  const refresh = useCallback(() => setBarVisible([...entries.current.values()].some((e) => e.dirty && e.bar)), []);
  const isDirty = useCallback(() => [...entries.current.values()].some((e) => e.dirty), []);
  const saveAll = useCallback(async () => {
    for (const e of entries.current.values()) if (e.dirty) await e.save?.();
  }, []);
  const discardAll = useCallback(() => {
    for (const e of entries.current.values()) if (e.dirty) e.discard?.();
  }, []);

  const api = useMemo<Api>(
    () => ({
      register(id, e) { entries.current.set(id, e); refresh(); },
      unregister(id) { entries.current.delete(id); refresh(); },
      isDirty,
      saveAll,
      discardAll,
      async guard(fn) {
        if (!isDirty()) return fn();
        const r = await dialogs.choice({
          title: 'You have unsaved changes',
          tone: 'warning',
          message: 'If you leave now, your changes will be lost. Do you want to save them first?',
          actions: [
            { label: 'Keep editing', value: 'stay', cancel: true },
            { label: 'Discard changes', value: 'discard', tone: '' },
            { label: 'Save & continue', value: 'save', tone: 'primary' },
          ],
        });
        if (r === 'discard') { discardAll(); fn(); }
        else if (r === 'save') { await saveAll(); fn(); }
      },
    }),
    [dialogs, isDirty, saveAll, discardAll, refresh],
  );

  // Browser close/refresh can only use the browser's own prompt (platform restriction).
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty() && !window.__missionsReloading) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div className={`ui-dirty-bar${barVisible ? ' in' : ''}`} role="region" aria-label="Unsaved changes" aria-hidden={!barVisible}>
          <span className="txt">Unsaved changes</span>
          <button type="button" className="ui-btn ghost" onClick={() => { discardAll(); toast('Changes discarded', { type: 'info' }); }}>Discard</button>
          <button type="button" className="ui-btn primary" onClick={async () => { await saveAll(); toast('Changes saved'); }}>Save changes</button>
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}

declare global {
  interface Window {
    __missionsReloading?: boolean;
  }
}
